import React from "react";
import { createPortal } from "react-dom";
import { registerConfirmDialog } from "../lib/dialog";

// Asks what lib/dialog.js is told to ask. Mounted once, at the top of the app. Cancel is the safe
// default: it has the focus, and Escape or a click outside gives the same answer. Keys are handled
// before anything behind the dialog sees them, so Escape cannot also close the teammate editor it
// may be sitting on. Focus goes back to whatever opened it.
export default function ConfirmDialog() {
  const [queue, setQueue] = React.useState([]);
  const lastId = React.useRef(0);
  const box = React.useRef(null);
  const cancelButton = React.useRef(null);
  const open = React.useRef([]);
  const current = queue[0] || null;

  React.useEffect(() => {
    const stop = registerConfirmDialog((request) => {
      lastId.current += 1;
      const item = { ...request, id: lastId.current, returnTo: document.activeElement };
      open.current = [...open.current, item];
      setQueue(open.current);
    });
    return () => {
      stop();
      open.current.forEach((item) => item.resolve(false));
      open.current = [];
    };
  }, []);

  const answer = React.useCallback((value) => {
    const [item, ...rest] = open.current;
    if (!item) return;
    open.current = rest;
    setQueue(rest);
    if (item.returnTo && document.contains(item.returnTo)) item.returnTo.focus?.();
    item.resolve(value);
  }, []);

  const currentId = current?.id;
  React.useEffect(() => {
    if (currentId) cancelButton.current?.focus();
  }, [currentId]);

  React.useEffect(() => {
    if (!currentId) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        answer(false);
      } else if (event.key === "Tab") {
        event.preventDefault();
        event.stopPropagation();
        const buttons = [...(box.current?.querySelectorAll("button") || [])];
        const at = buttons.indexOf(document.activeElement);
        const next = event.shiftKey ? (at <= 0 ? buttons.length - 1 : at - 1) : (at + 1) % buttons.length;
        buttons[next]?.focus();
      } else if (event.key === "Enter" && event.repeat) {
        // Still held from the press that opened this, which is not an answer.
        event.preventDefault();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [currentId, answer]);

  if (!current) return null;

  const titleId = `bd-confirm-title-${current.id}`;
  const bodyId = `bd-confirm-body-${current.id}`;
  return createPortal(
    <div
      className="bdConfirmOverlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) answer(false);
      }}
    >
      <div
        ref={box}
        className="bdConfirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={current.body ? bodyId : undefined}
      >
        <h2 id={titleId} className="bdConfirm__title">
          {current.title}
        </h2>
        {current.body ? (
          <p id={bodyId} className="bdConfirm__body">
            {current.body}
          </p>
        ) : null}
        <div className="bdConfirm__actions">
          <button ref={cancelButton} type="button" className="btn" onClick={() => answer(false)}>
            Cancel
          </button>
          <button
            type="button"
            className={current.danger ? "btn btnDanger" : "btn primary"}
            onClick={() => answer(true)}
          >
            {current.confirmLabel || "Confirm"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
